import {
  type GaugeMetricData,
  type GaugeMetricsMap,
} from '@/warranty/domain/entities/gauge-metric';
import { tailwindColorMap } from '@/warranty/view/components/tailwind.colors';

export interface PerformanceWarrantyStateRow {
  'platform_core_warranty_unit_status.soh_accure_agg': number;
  'platform_core_warranty_unit_status.soh_warranty_avg': number;
  'tag.value_avg': number | undefined;
  'tag.threshold_avg': number | undefined;
  'rte.value_avg': number | undefined;
  'rte.threshold_avg': number | undefined;
}

export interface RawWarrantyCaseAlertsRow {
  'platform_xfleet_warranty_case_alerts.value_avg': number;
  'platform_xfleet_warranty_case_alerts.threshold_avg': number;
}

function calculateMinValueForSohGauge(input: {
  sohAccureAgg: number;
  sohWarrantyAvg: number;
}): number {
  const minSohValue = Math.min(input.sohAccureAgg, input.sohWarrantyAvg);

  if (minSohValue >= 75) {
    return 0;
  } else if (minSohValue >= 50) {
    return 50;
  } else if (minSohValue >= 25) {
    return 70;
  } else {
    return 90;
  }
}

export function transformPerformanceWarrantyState(
  result: PerformanceWarrantyStateRow,
  customerId?: string,
): GaugeMetricsMap {
  const minValue = calculateMinValueForSohGauge({
    sohAccureAgg:
      result['platform_core_warranty_unit_status.soh_accure_agg'] ?? 0,
    sohWarrantyAvg:
      result['platform_core_warranty_unit_status.soh_warranty_avg'] ?? 0,
  });

  const sohMetric: GaugeMetricData = {
    id: 'SOH',
    groupFullId: '',
    longName: 'State of Health',
    state:
      result['platform_core_warranty_unit_status.soh_warranty_avg'] === null
        ? 'inactive'
        : 'active',
    yearToDate:
      result['platform_core_warranty_unit_status.soh_accure_agg'] ?? undefined,
    futurePrediction: undefined,
    predictionWindowEnd: undefined,
    min: minValue,
    max: 100,
    zones: [
      {
        from: 0,
        to: result['platform_core_warranty_unit_status.soh_warranty_avg'],
        color: tailwindColorMap.warning,
      },
      {
        from: result['platform_core_warranty_unit_status.soh_warranty_avg'],
        to: 100,
        color: tailwindColorMap.ok,
      },
    ],
  };

  const rteMetric: GaugeMetricData = {
    id: 'RTE',
    groupFullId: '',
    longName: 'Round Trip Efficiency',
    state: typeof result['rte.value_avg'] === 'number' ? 'active' : 'inactive',
    yearToDate: result['rte.value_avg'] ?? undefined,
    futurePrediction: undefined,
    predictionWindowEnd: undefined,
    min: 70,
    max: 100,
    zones: [
      {
        from: 70,
        to: result['rte.threshold_avg'] ?? 100,
        color: tailwindColorMap.warning,
      },
      {
        from: result['rte.threshold_avg'] ?? 100,
        to: 100,
        color: tailwindColorMap.ok,
      },
    ],
  };

  const tagMetric: GaugeMetricData = {
    id: 'TAG',
    groupFullId: '',
    longName:
      customerId === '69'
        ? 'Technical PCS Availability Guarantee'
        : 'Technical Availability Guarantee',
    state: typeof result['tag.value_avg'] === 'number' ? 'active' : 'inactive',
    yearToDate: result['tag.value_avg'] ?? undefined,
    futurePrediction: undefined,
    predictionWindowEnd: undefined,
    min: 70,
    max: 100,
    zones: [
      {
        from: 0,
        to: result['tag.threshold_avg'] ?? 100,
        color: tailwindColorMap.warning,
      },
      {
        from: result['tag.threshold_avg'] ?? 100,
        to: 100,
        color: tailwindColorMap.ok,
      },
    ],
  };

  return {
    SOH: sohMetric,
    RTE: rteMetric,
    TAG: tagMetric,
  };
}
